import { useCallback, useMemo } from "react";
import { atom, useRecoilState } from "recoil";

export const emptyFilters: Filters = {
    booleanFilters: [],
    dataCoverageFilter: [],
    dateFilters: [],
    numericFilters: [],
    stringFilters: [],
};

const buildingModelFiltersAtom = atom<Filters>({
    key: "buildingModelsFilters",
    default: emptyFilters,
});

const buildingModelFiltersVersionAtom = atom<number>({
    key: "buildingModelsFiltersVersion",
    default: 1,
});

const useFilters = () => {
    const [filters, setFiltersInternal] = useRecoilState(buildingModelFiltersAtom);
    const [filtersVersion, setFiltersVersion] = useRecoilState(buildingModelFiltersVersionAtom);

    const setFilters = (newFilters: Filters) => {
        const hasEnergyTypeFilter = newFilters.stringFilters.some((filter) => filter.field === "energy_type");
        if (hasEnergyTypeFilter) {
            const modifiedFilters = JSON.parse(JSON.stringify(newFilters));
            for (let i = 0; i < modifiedFilters.stringFilters.length; i++) {
                const currentStringFilter = modifiedFilters.stringFilters[i];
                if (currentStringFilter.field === "energy_type") {
                    if (currentStringFilter.value.includes("electric")) {
                        currentStringFilter.value.push("electric_inferred");
                    }
                    if (currentStringFilter.value.includes("mixed")) {
                        currentStringFilter.value.push("mixed_inferred");
                    }
                    break;
                }
            }
            setFiltersInternal(modifiedFilters);
        } else {
            setFiltersInternal(newFilters);
        }
        setFiltersVersion(filtersVersion + 1);
    };

    const removeFilters = useCallback(() => {
        setFilters(emptyFilters);
    }, []);

    const removeFilter = (filterField: StringFilterField | NumericFilterField | DateFilterField | "data_coverage") => {
        const copy = {
            booleanFilters: [...filters.booleanFilters],
            dateFilters: [...filters.dateFilters],
            dataCoverageFilter: [...filters.dataCoverageFilter],
            numericFilters: [...filters.numericFilters],
            stringFilters: [...filters.stringFilters],
        };
        if (filterField === "data_coverage") {
            copy.dataCoverageFilter = [];
        }

        const numericIndex = copy.numericFilters.findIndex(({ field }) => field === filterField);

        if (numericIndex !== -1) {
            copy.numericFilters.splice(numericIndex, 1);
        }

        const stringIndex = copy.stringFilters.findIndex(({ field }) => field === filterField);

        if (stringIndex !== -1) {
            copy.stringFilters.splice(stringIndex, 1);
        }

        const dateIndex = copy.dateFilters.findIndex(({ field }) => field === filterField);

        if (dateIndex !== -1) {
            copy.dateFilters.splice(dateIndex, 1);
        }
        setFilters(copy);
    };

    const numberOfFilters = useMemo(() => {
        const { dataCoverageFilter, numericFilters, stringFilters, dateFilters } = filters;
        return dataCoverageFilter.length + numericFilters.length + stringFilters.length + dateFilters.length;
    }, [filters]);

    return {
        filters,
        filtersVersion,
        setFilters,
        removeFilters,
        removeFilter,
        numberOfFilters,
    };
};

export default useFilters;
